import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { PhoneInput } from '../components/PhoneInput';
import { isValidE164 } from '../lib/validatePhone';

type ChannelKind = 'whatsapp' | 'email';

export function LandingPage() {
  const { t, i18n } = useTranslation('landing');
  const navigate = useNavigate();

  // The blog and docs sites both have a localized variant for every
  // locale we ship. Cross-app footer links point straight at the
  // visitor's current locale instead of forcing an English landing
  // → CloudFront → JS-side redirect round-trip. Docs URLs preserve
  // case (/docs/es-ES/), blog URLs are lowercased (/blog/es-es/).
  const currentLang = i18n.language || 'en';
  const docsHref = currentLang === 'en' ? '/docs/' : `/docs/${currentLang}/`;
  const blogHref = currentLang === 'en' ? '/blog/' : `/blog/${currentLang.toLowerCase()}/`;

  const [channel, setChannel] = useState<ChannelKind>('whatsapp');
  const [xUsername, setXUsername] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [swapFade, setSwapFade] = useState(false);
  const [error, setError] = useState('');

  const swapChannel = () => {
    setSwapFade(true);
    setTimeout(() => {
      setChannel(c => (c === 'whatsapp' ? 'email' : 'whatsapp'));
      setSwapFade(false);
    }, 110);
  };

  const handleStart = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const cleanUsername = xUsername.replace(/^@/, '').trim();
    // Email is now collected on EVERY signup, whichever delivery channel
    // the user picks. WhatsApp alerts are subject to Meta's per-recipient
    // #131049 cap, which silently drops messages for exactly the heaviest
    // users and cannot be fixed template-side — so an account with no
    // email has no route to delivery at all once it is throttled. `channel`
    // now means "where alerts go first", not "the only thing we collect".
    const cleanPhone = channel === 'whatsapp' ? phoneValue.replace(/\s+/g, '').trim() : '';
    const cleanEmail = emailValue.trim().toLowerCase();

    if (channel === 'whatsapp' && !isValidE164(cleanPhone)) {
      setError(t('hero.form.invalidPhone', {
        defaultValue: 'Please enter a valid phone number.',
      }));
      return;
    }
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError(t('hero.form.invalidEmail', {
        defaultValue: 'Please enter a valid email address.',
      }));
      return;
    }

    // Persist the pre-signup intent so DashboardPage can auto-create the
    // X subscription once the user is authenticated. The identifier
    // destination is handled by the Custom Auth flow (phone or email
    // becomes the Cognito username and, via VerifyAuthChallengeResponse,
    // a verified primary Channel row) — we only carry xUsername here.
    if (cleanUsername || cleanPhone || cleanEmail) {
      sessionStorage.setItem(
        'wallawhats_intent',
        JSON.stringify({
          xUsername: cleanUsername,
          channel,
          phoneNumber: cleanPhone,
          email: cleanEmail,
        }),
      );
    }

    const params = new URLSearchParams();
    if (cleanUsername) params.set('x', cleanUsername);
    params.set('channel', channel);
    if (cleanPhone) params.set('phone', cleanPhone);
    if (cleanEmail) params.set('email', cleanEmail);
    navigate(`/signup?${params.toString()}`);
  };

  return (
    <div className="min-h-screen text-gray-900">
      <div className="bg-gradient-to-b from-brand-100 via-brand-50 to-white">
        {/* Header */}
        <header className="border-b border-brand-200/60 bg-brand-100/70 backdrop-blur sticky top-0 z-40">
          <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
            <Link to="/">
              <img src="/assets/logo/logo-full-transparent.png" alt="WallaWhats" className="h-9" />
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link to="/signin" className="hidden sm:inline text-sm font-medium text-gray-700 hover:text-gray-900">
                {t('nav.signIn')}
              </Link>
              <Link
                to="/signup"
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
              >
                {t('nav.signUp')}
              </Link>
            </div>
          </nav>
        </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative mx-auto max-w-5xl px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <h1 className="text-center text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900">
            <Trans
              i18nKey="hero.headline"
              ns="landing"
              components={{ accent: <span className="text-brand-500" /> }}
            />
          </h1>

          <p className="mt-6 text-center text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            <Trans
              i18nKey="hero.subtitleTemplate"
              ns="landing"
              components={{
                br: <br className="hidden sm:block" />,
                small: <span className="block mt-2 text-sm sm:text-base text-gray-400" />,
              }}
            />
          </p>

          {/* Workflow diagram */}
          <div className="mt-14 flex items-start justify-center gap-2 sm:gap-4">
            <WorkflowStep
              icon={
                <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              }
              iconBg="bg-gray-900"
              label={t('hero.workflow.xPost')}
              caption={t('hero.workflow.xPostCaption')}
            />

            <FlowConnector />

            <WorkflowStep
              icon={<img src="/assets/logo/ninja-icon-128.png" alt="" className="h-16 w-16 sm:h-[72px] sm:w-[72px]" />}
              iconBg=""
              iconWrapperClass="h-16 w-16 sm:h-[72px] sm:w-[72px]"
              label={t('hero.workflow.walla')}
              caption={t('hero.workflow.wallaCaption')}
            />

            <FlowConnector />

            {/* Third step: flips between WhatsApp and Email based on channel state */}
            <div className="flex flex-col items-center w-24 sm:w-28">
              <div className={`flex h-16 w-16 sm:h-[72px] sm:w-[72px] items-center justify-center rounded-2xl ${channel === 'whatsapp' ? 'bg-[#25D366]' : 'bg-brand-500'} text-white shadow-lg shadow-gray-300/40`}>
                {channel === 'whatsapp' ? <WhatsAppLargeIcon /> : <EmailLargeIcon />}
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-900">{channel === 'whatsapp' ? t('hero.workflow.whatsapp') : t('hero.workflow.email')}</p>
              <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-brand-600">{t('hero.workflow.whatsappCaption')}</p>
            </div>
          </div>

          {/* Secondary row: centered timing overlay + "or email/WhatsApp" aligned under 3rd col */}
          <div className="relative mt-5 hidden sm:flex items-center justify-center gap-4">
            <div className="w-28"></div>
            <div className="flex-1 max-w-[90px]"></div>
            <div className="w-28"></div>
            <div className="flex-1 max-w-[90px]"></div>
            <div className="w-28 flex justify-center">
              <button
                type="button"
                onClick={swapChannel}
                className="flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity"
                title={channel === 'whatsapp' ? t('hero.workflow.orEmail') : t('hero.workflow.orWhatsapp')}
              >
                <span className="text-[10px] uppercase tracking-widest text-gray-400">{t('hero.workflow.orLabel')}</span>
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-500 shadow-sm">
                  {channel === 'whatsapp' ? <EmailMiniIcon /> : <WhatsAppMiniIcon />}
                </div>
                <span className="text-[11px] font-medium text-gray-500">
                  {channel === 'whatsapp' ? t('hero.workflow.email').toLowerCase() : t('hero.workflow.whatsapp')}
                </span>
              </button>
            </div>
            <p className="absolute inset-0 flex items-center justify-center text-xs font-medium uppercase tracking-widest text-gray-400 pointer-events-none">
              {t('hero.workflow.timing')}
            </p>
          </div>
          <div className="mt-5 flex sm:hidden flex-col items-center gap-3">
            <p className="text-xs font-medium uppercase tracking-widest text-gray-400">{t('hero.workflow.timing')}</p>
            <button
              type="button"
              onClick={swapChannel}
              className="flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity"
            >
              <span className="text-[10px] uppercase tracking-widest text-gray-400">{t('hero.workflow.orLabel')}</span>
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-500 shadow-sm">
                {channel === 'whatsapp' ? <EmailMiniIcon /> : <WhatsAppMiniIcon />}
              </div>
              <span className="text-[11px] font-medium text-gray-500">
                {channel === 'whatsapp' ? t('hero.workflow.email').toLowerCase() : t('hero.workflow.whatsapp')}
              </span>
            </button>
          </div>

          {/* Interactive form */}
          <form
            onSubmit={handleStart}
            className="mt-12 mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-xl shadow-gray-200/40"
          >
            <div className="space-y-5">
              <div>
                <label htmlFor="x-username" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500 text-white text-xs font-bold">1</span>
                  {t('hero.form.xLabel')}
                </label>
                <div className="mt-2 relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                  <input
                    id="x-username"
                    type="text"
                    value={xUsername}
                    onChange={(e) => setXUsername(e.target.value.replace(/^@/, ''))}
                    placeholder={t('hero.form.xPlaceholder')}
                    className="w-full rounded-lg border border-gray-300 bg-white pl-8 pr-4 py-3 text-base focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                  />
                </div>
              </div>

              {/* Step 2: channel-aware. Wrapped in a fading div so the swap is visibly smooth. */}
              <div className={`transition-opacity duration-[110ms] ${swapFade ? 'opacity-0' : 'opacity-100'}`}>
                <label htmlFor="channel-input" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500 text-white text-xs font-bold">2</span>
                  {channel === 'whatsapp' ? <WhatsAppSmallIcon /> : <EmailSmallIcon />}
                  {channel === 'whatsapp' ? t('hero.form.phoneLabel') : t('hero.form.emailLabel')}
                </label>
                {channel === 'whatsapp' && (
                  <>
                    <PhoneInput
                      id="channel-input"
                      value={phoneValue}
                      onChange={setPhoneValue}
                      placeholder={t('hero.form.phonePlaceholder')}
                      className="mt-2 text-base [&>button]:py-3 [&>input]:py-3"
                      required
                    />
                    <p className="mt-1.5 text-xs text-gray-500">{t('hero.form.phoneHint')}</p>
                  </>
                )}
                {/* Email is always collected — see handleStart. When WhatsApp
                    is the chosen channel this is the backup that keeps alerts
                    arriving if Meta throttles the number. */}
                <input
                  id={channel === 'whatsapp' ? 'email-backup-input' : 'channel-input'}
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder={t('hero.form.emailPlaceholder')}
                  className={`${channel === 'whatsapp' ? 'mt-3' : 'mt-2'} w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-base focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition`}
                  required
                  aria-label={t('hero.form.emailLabel')}
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  {channel === 'whatsapp'
                    ? t('hero.form.emailBackupHint', {
                        defaultValue: "For your account, and as a backup if WhatsApp can't deliver.",
                      })
                    : t('hero.form.emailHint')}
                </p>
                <p className="mt-2 text-xs">
                  <button
                    type="button"
                    onClick={swapChannel}
                    className="text-brand-600 hover:text-brand-700 transition-colors underline underline-offset-2 decoration-brand-400 hover:decoration-brand-700"
                  >
                    {channel === 'whatsapp' ? t('hero.form.preferEmail') : t('hero.form.preferWhatsapp')}
                  </button>
                </p>
              </div>

              {error && <p className="text-sm text-alert-500">{error}</p>}
              <button
                type="submit"
                className="w-full rounded-lg bg-primary-600 px-6 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 group"
              >
                {t('hero.form.submit')}
                <svg className="h-5 w-5 transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </button>

              <p className="text-center text-xs text-gray-500">
                <span className="font-semibold text-accent-500">{t('hero.form.freeForever')}</span> {t('hero.form.freeNote')}
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Why WallaWhats — explicit problem/solution framing.
          Sits between the hero (what + form) and How-It-Works
          (onboarding) so the visitor reads the why before the how. */}
      <section className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
        <div className="text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
            {t('whyWalla.title')}
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {t('whyWalla.description')}
          </p>
        </div>

        <div className="mt-10 rounded-2xl border border-gray-200 bg-gray-50/60 p-6 sm:p-8">
          <h3 className="text-base font-semibold text-gray-900">
            {t('whyWalla.bulletsTitle')}
          </h3>
          <ul className="mt-4 space-y-2.5">
            {(['missed', 'late', 'refresh', 'thread'] as const).map((key) => (
              <li key={key} className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-500" aria-hidden="true" />
                <span className="text-sm text-gray-700 leading-relaxed">
                  <Trans
                    i18nKey={`whyWalla.bullets.${key}`}
                    ns="landing"
                    components={{ b: <strong className="font-semibold text-gray-900" /> }}
                  />
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-gray-600 leading-relaxed">
            {t('whyWalla.footer')}
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        <h2 className="text-center text-3xl sm:text-4xl font-bold tracking-tight">{t('howItWorks.title')}</h2>
        <p className="mt-3 text-center text-gray-600 max-w-xl mx-auto">
          {t('howItWorks.subtitle')}
        </p>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <StepCard n={1} title={t('howItWorks.steps.signUp.title')} description={t('howItWorks.steps.signUp.description')} />
          <StepCard
            n={2}
            title={t(channel === 'whatsapp' ? 'howItWorks.steps.verify.title' : 'howItWorks.steps.verifyEmail.title')}
            description={t(channel === 'whatsapp' ? 'howItWorks.steps.verify.description' : 'howItWorks.steps.verifyEmail.description')}
          />
          <StepCard n={3} title={t('howItWorks.steps.monitor.title')} description={t('howItWorks.steps.monitor.description')} />
        </div>
      </section>

      {/* Use cases */}
      <section>
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <h2 className="text-center text-3xl sm:text-4xl font-bold tracking-tight">{t('useCases.title')}</h2>
          <p className="mt-3 text-center text-gray-600 max-w-2xl mx-auto">
            {t('useCases.subtitle')}
          </p>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <UseCaseCard icon={<NewspaperIcon />} title={t('useCases.cases.news.title')} subtitle={t('useCases.cases.news.subtitle')} body={t('useCases.cases.news.body')} examples={['@BBCBreaking', '@CNN', '@nytimes']} examplesLabel={t('useCases.examplesLabel')} />
            <UseCaseCard icon={<TrendingUpIcon />} title={t('useCases.cases.traders.title')} subtitle={t('useCases.cases.traders.subtitle')} body={t('useCases.cases.traders.body')} examples={['@elonmusk', '@WSJ', '@business']} examplesLabel={t('useCases.examplesLabel')} />
            <UseCaseCard icon={<StarIcon />} title={t('useCases.cases.fans.title')} subtitle={t('useCases.cases.fans.subtitle')} body={t('useCases.cases.fans.body')} examples={['@Cristiano', '@rihanna', '@justinbieber']} examplesLabel={t('useCases.examplesLabel')} />
            <UseCaseCard icon={<ShieldCheckIcon />} title={t('useCases.cases.brand.title')} subtitle={t('useCases.cases.brand.subtitle')} body={t('useCases.cases.brand.body')} examples={['@tim_cook', '@satyanadella', '@pmarca']} examplesLabel={t('useCases.examplesLabel')} />
          </div>
          {/* Persona deep-dive links — internal linking from the
              homepage helps Google discover the dedicated /for-* pages
              via the high-frequency landing crawl. */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-gray-600">
            <span className="font-medium text-gray-700">{t('useCases.personaLinksLabel', { defaultValue: 'Built for:' })}</span>
            <Link to="/for-journalists" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.journalists', { defaultValue: 'Journalists' })}
            </Link>
            <Link to="/for-traders" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.traders', { defaultValue: 'Traders' })}
            </Link>
            <Link to="/for-marketers" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.marketers', { defaultValue: 'Marketing teams' })}
            </Link>
            <Link to="/for-founders" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.founders', { defaultValue: 'Founders' })}
            </Link>
            <Link to="/for-sales" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.sales', { defaultValue: 'Sales teams' })}
            </Link>
            <Link to="/for-recruiters" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.recruiters', { defaultValue: 'Recruiters' })}
            </Link>
            <Link to="/for-developers" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.developers', { defaultValue: 'Developers' })}
            </Link>
            <Link to="/for-investors" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.investors', { defaultValue: 'Investors & VCs' })}
            </Link>
            <Link to="/for-pr" className="rounded-full border border-gray-200 px-4 py-1.5 hover:border-gray-300 hover:bg-gray-50 transition-colors">
              {t('useCases.personaLinks.pr', { defaultValue: 'PR & comms' })}
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        <h2 className="text-center text-3xl sm:text-4xl font-bold tracking-tight">{t('pricing.title')}</h2>
        <p className="mt-3 text-center text-gray-600">{t('pricing.subtitle')}</p>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {(['free', 'mid', 'pro_plus', 'top', 'enterprise'] as const).map(plan => (
            <PlanCard
              key={plan}
              name={t(`pricing.plans.${plan}.name`)}
              price={t(`pricing.plans.${plan}.price`)}
              description={t(`pricing.plans.${plan}.description`)}
              features={t(`pricing.plans.${plan}.features`, { returnObjects: true }) as string[]}
              perMonth={t('pricing.perMonth')}
              popularLabel={t('pricing.popularBadge')}
              highlight={plan === 'pro_plus'}
            />
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <h2 className="text-center text-3xl sm:text-4xl font-bold tracking-tight">{t('faq.title')}</h2>
          <div className="mt-10 space-y-3">
            <FAQItem q={t('faq.items.speed.q')} a={t('faq.items.speed.a')} />
            <FAQItem q={t('faq.items.business.q')} a={t('faq.items.business.a')} />
            <FAQItem q={t('faq.items.limit.q')} a={t('faq.items.limit.a')} />
            <FAQItem q={t('faq.items.cancel.q')} a={t('faq.items.cancel.a')} />
            <FAQItem q={t('faq.items.privacy.q')} a={t('faq.items.privacy.a')} />
          </div>
        </div>
      </section>
      </div>

      {/* Final CTA */}
      <section className="bg-brand-500">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
            {t('finalCta.title')}
          </h2>
          <p className="mt-3 text-brand-50">{t('finalCta.subtitle')}</p>
          <Link
            to="/signup"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-base font-semibold text-brand-700 hover:bg-gray-50 transition-colors"
          >
            {t('finalCta.cta')}
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 text-sm text-gray-500 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/assets/logo/ninja-icon-32.png" alt="" className="h-5 w-5" />
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-gray-700">{t('footer.privacy')}</Link>
            <Link to="/terms-of-service" className="hover:text-gray-700">{t('footer.terms')}</Link>
            <a href={docsHref} className="hover:text-gray-700">{t('footer.docs')}</a>
            <a href={blogHref} className="hover:text-gray-700">{t('footer.blog')}</a>
            <a href="/blog/about" className="hover:text-gray-700">{t('footer.about', { defaultValue: 'About' })}</a>
          </div>
        </div>
        <div className="border-t border-gray-100">
          <p className="mx-auto max-w-6xl px-6 py-4 text-center text-xs text-gray-400">
            <Trans
              i18nKey="footer.poweredBy"
              ns="landing"
              components={{
                bws: (
                  <a
                    href="https://www.bws.ninja"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-underline hover:text-gray-600 transition-colors"
                  />
                ),
              }}
            />
          </p>
        </div>
      </footer>
    </div>
  );
}

function WorkflowStep({
  icon, iconBg, iconWrapperClass = 'h-16 w-16 sm:h-[72px] sm:w-[72px]', label, caption,
}: {
  icon: React.ReactNode; iconBg: string; iconWrapperClass?: string; label: string; caption: string;
}) {
  return (
    <div className="flex flex-col items-center w-24 sm:w-28">
      <div className={`flex ${iconWrapperClass} items-center justify-center ${iconBg ? `${iconBg} rounded-2xl shadow-lg shadow-gray-300/40` : ''} text-white`}>
        {icon}
      </div>
      <p className="mt-4 text-sm font-semibold text-gray-900">{label}</p>
      <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-brand-600">{caption}</p>
    </div>
  );
}

function FlowConnector() {
  return (
    <div className="hidden sm:flex flex-1 max-w-[90px] items-center pt-7">
      <div className="relative w-full">
        <svg className="w-full h-3" viewBox="0 0 100 12" preserveAspectRatio="none" aria-hidden="true">
          <line x1="0" y1="6" x2="92" y2="6" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4 4" />
          <polyline points="88,2 94,6 88,10" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

function StepCard({ n, title, description }: { n: number; title: string; description: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white font-bold shadow-sm">{n}</div>
      <h3 className="mt-4 text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
}

function UseCaseCard({
  icon, title, subtitle, body, examples, examplesLabel,
}: {
  icon: React.ReactNode; title: string; subtitle: string; body: string; examples: string[]; examplesLabel: string;
}) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-6 sm:p-7 flex flex-col gap-4 hover:border-brand-200 hover:shadow-md hover:shadow-brand-500/5 transition-all">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-brand-600 flex-shrink-0">{icon}</div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <p className="text-sm font-medium text-brand-600 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
      <div className="flex flex-wrap gap-1.5 mt-auto pt-2 border-t border-gray-100">
        <span className="text-xs font-medium text-gray-400 mr-1">{examplesLabel}</span>
        {examples.map(ex => (
          <span key={ex} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">{ex}</span>
        ))}
      </div>
    </div>
  );
}

function NewspaperIcon() { return (<svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3V12H6V7.5Z" /></svg>); }
function TrendingUpIcon() { return (<svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" /></svg>); }
function StarIcon() { return (<svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>); }
function ShieldCheckIcon() { return (<svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.063 2.522-.189 3.758a3.745 3.745 0 0 1-3.053 3.053 48.46 48.46 0 0 1-7.516 0 3.745 3.745 0 0 1-3.053-3.053A48.615 48.615 0 0 1 3 12c0-1.268.063-2.522.189-3.758a3.745 3.745 0 0 1 3.053-3.053 48.46 48.46 0 0 1 7.516 0 3.745 3.745 0 0 1 3.053 3.053C20.937 9.478 21 10.732 21 12Z" /></svg>); }
function WhatsAppSmallIcon() { return (<svg className="h-4 w-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>); }
function EmailSmallIcon() { return (<svg className="h-4 w-4 text-brand-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>); }
function WhatsAppLargeIcon() { return (<svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>); }
function EmailLargeIcon() { return (<svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>); }
function EmailMiniIcon() { return (<svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>); }
function WhatsAppMiniIcon() { return (<svg className="h-5 w-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>); }

function PlanCard({ name, price, perMonth, description, features, highlight = false, popularLabel }: {
  name: string;
  price: string;
  perMonth: string;
  description: string;
  features: string[];
  highlight?: boolean;
  popularLabel: string;
}) {
  return (
    <div data-testid="pricing-tier" className={`relative flex flex-col rounded-2xl border-2 p-6 ${highlight ? 'border-brand-500 bg-brand-50/40 shadow-lg shadow-brand-500/10' : 'border-gray-200 bg-white'}`}>
      {highlight && (<span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">{popularLabel}</span>)}
      <h3 className="font-semibold">{name}</h3>
      <p className="mt-3 text-3xl font-bold">{price}<span className="text-base font-normal text-gray-500">{perMonth}</span></p>
      <p className="mt-2 text-xs text-gray-500 leading-relaxed">{description}</p>
      <ul className="mt-4 space-y-2 text-sm text-gray-600">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <svg className="h-4 w-4 flex-shrink-0 text-brand-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
            </svg>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  return (
    <details className="group rounded-xl bg-white border border-gray-200 p-5 [&_svg]:open:rotate-45 open:shadow-sm">
      <summary className="flex cursor-pointer items-center justify-between font-semibold text-gray-900 list-none">
        {q}
        <svg className="h-5 w-5 text-gray-400 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
      </summary>
      <p className="mt-3 text-sm text-gray-600 leading-relaxed">{a}</p>
    </details>
  );
}
